import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, X, Maximize2,
  BarChart3, Upload, Map, Table2, Flame, MapPin,
  Calendar, Flag, Search, Send, Database, Shield,
} from 'lucide-react';
import { LOGO_URL } from '../utils/brand';

const FEATURES = [
  {
    title: 'Upload & Process Data',
    description: 'Import FEC CSV datasets with async background processing. Bulk upload from URLs or drag-and-drop files. Automatic parsing and normalization of contribution records.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/iokycr10_image.png',
  },
  {
    title: 'Interactive Dashboard',
    description: 'View summary statistics at a glance. Top donors, committee breakdowns, contribution trends over time, and party-level analytics with charts and graphs.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/t7enk4ba_image.png',
  },
  {
    title: 'Donor Map',
    description: 'Street-level geocoded pins on an interactive map. Color-coded by activity level or political party. Zoom, filter, and click any pin to see full donation history.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/a394s62j_image.png',
  },
  {
    title: 'Investigation Tools',
    description: 'Deep-dive with heat maps showing dollar concentration and donation count. Investigation refinement panel to filter by committee, year, amount, and more.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/2db3jylg_image.png',
  },
  {
    title: 'Year Filtering',
    description: 'Select one year, multiple years, or all years to track how donation patterns change over time. Filter applies across the entire map view instantly.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/uxke1k8g_image.png',
  },
  {
    title: 'Push to Production',
    description: 'Selectively sync specific datasets from Preview to Production environments. Streaming architecture handles datasets of 300K+ records reliably.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/q9xxrqvh_image.png',
  },
  {
    title: 'Multi-Role Access',
    description: 'Three-tier role system: Superadmin for full control, User for data upload and analysis, Viewer for read-only access. All roles see all datasets globally.',
    image: 'https://customer-assets.emergentagent.com/job_48044ce1-0d4b-4d76-a21e-c0ed7372a351/artifacts/zsi2gaku_image.png',
  },
];

const GRID_FEATURES = [
  { icon: BarChart3, title: 'Dashboard', desc: 'Charts & analytics' },
  { icon: Upload, title: 'Data Upload', desc: 'CSV & URL import' },
  { icon: Map, title: 'Donor Map', desc: 'Geocoded pin map' },
  { icon: Table2, title: 'Data Table', desc: 'Search & filter' },
  { icon: Flame, title: 'Heat Maps', desc: 'Dollar density' },
  { icon: MapPin, title: 'Geocoding', desc: 'Street-level pins' },
  { icon: Calendar, title: 'Year Filter', desc: 'Track over time' },
  { icon: Flag, title: 'Party Colors', desc: 'Dem / Rep / Mixed' },
  { icon: Search, title: 'Investigation', desc: 'Deep-dive analysis' },
  { icon: Send, title: 'Push to Prod', desc: 'Selective sync' },
  { icon: Database, title: 'Smart Caching', desc: 'Fast aggregations' },
  { icon: Shield, title: 'Role Access', desc: 'Admin / User / View' },
];

export default function LearnMorePage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [lightbox, setLightbox] = useState(null); // null = closed, index = open

  const openLightbox = (index) => { setLightbox(index); setIsPaused(true); };
  const closeLightbox = () => { setLightbox(null); setIsPaused(false); };
  const lightboxNext = useCallback(() => setLightbox(prev => (prev + 1) % FEATURES.length), []);
  const lightboxPrev = useCallback(() => setLightbox(prev => (prev - 1 + FEATURES.length) % FEATURES.length), []);

  useEffect(() => {
    if (lightbox === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') lightboxNext();
      if (e.key === 'ArrowLeft') lightboxPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightbox, lightboxNext, lightboxPrev]);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % FEATURES.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + FEATURES.length) % FEATURES.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide]);

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="learn-more-page">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes zoomIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {/* Lightbox Modal */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: 'rgba(2,6,15,0.92)', backdropFilter: 'blur(12px)' }}
          onClick={closeLightbox}
          data-testid="lightbox-overlay"
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Title bar */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10">
            <span className="text-xs font-semibold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-full px-3 py-1">
              {lightbox + 1} / {FEATURES.length}
            </span>
            <span className="text-sm font-semibold text-white">{FEATURES[lightbox].title}</span>
          </div>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Image */}
          <img
            key={lightbox}
            src={FEATURES[lightbox].image}
            alt={FEATURES[lightbox].title}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl"
            style={{ animation: 'zoomIn 0.25s ease-out' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Thumbnail strip */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {FEATURES.map((f, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setLightbox(i); }}
                className={`w-16 h-10 rounded-md overflow-hidden border-2 transition-all ${
                  i === lightbox ? 'border-sky-400 opacity-100 scale-105' : 'border-transparent opacity-40 hover:opacity-70'
                }`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={f.image} alt={f.title} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0a1628]/90 backdrop-blur-md border-b border-[#1a3550]/50">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
          <div className="flex items-center gap-2">
            <img
              src={LOGO_URL}
              alt="FEC Watchdog"
              className="h-8 w-auto object-contain select-none"
              draggable={false}
              data-testid="learn-nav-logo"
            />
          </div>
          <button
            onClick={() => navigate('/login')}
            className="px-5 py-1.5 bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold rounded-full transition-colors"
            data-testid="get-started-btn"
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-16 pb-8 text-center px-6">
        <img
          src={LOGO_URL}
          alt="FEC Watchdog"
          className="mx-auto mb-6 w-72 sm:w-96 h-auto object-contain drop-shadow-[0_0_40px_rgba(14,165,233,0.35)] select-none"
          draggable={false}
          data-testid="learn-hero-logo"
        />
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-cyan-400 to-sky-400 bg-clip-text text-transparent leading-tight">
          Campaign Finance Intelligence
        </h1>
        <p className="mt-4 text-slate-400 text-base lg:text-lg max-w-2xl mx-auto">
          Analyze voter data from the Federal Election Commission. Upload CSV datasets,
          view summary statistics, and perform deep-dive analysis on an interactive map.
        </p>
      </section>

      {/* Feature Carousel */}
      <section
        className="max-w-6xl mx-auto px-6 pb-16"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="relative bg-[#0f2035] rounded-2xl border border-[#1a3550] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[340px]">
            {/* Left: Visual placeholder */}
            <div className="lg:col-span-3 bg-gradient-to-br from-[#0c1a2e] to-[#162d44] flex items-center justify-center p-4 relative overflow-hidden">
              <div
                className="relative cursor-pointer group/img w-full h-full flex items-center justify-center"
                onClick={() => openLightbox(currentSlide)}
              >
                <img
                  key={currentSlide}
                  src={FEATURES[currentSlide].image}
                  alt={FEATURES[currentSlide].title}
                  className="w-full h-full object-contain rounded-lg max-h-[320px] animate-[fadeIn_0.3s_ease-in-out] group-hover/img:brightness-110 transition-all"
                  data-testid="carousel-image"
                />
                <div className="absolute inset-0 rounded-lg opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center bg-black/20">
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-white bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5">
                    <Maximize2 className="w-3.5 h-3.5" /> Click to expand
                  </span>
                </div>
              </div>

              {/* Nav Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0a1628]/80 border border-[#1a3550] flex items-center justify-center text-slate-300 hover:text-white hover:border-sky-500/50 transition-colors"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0a1628]/80 border border-[#1a3550] flex items-center justify-center text-slate-300 hover:text-white hover:border-sky-500/50 transition-colors"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Right: Feature description */}
            <div className="lg:col-span-2 p-5 md:p-8 flex flex-col justify-center">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-full px-3 py-1 w-fit mb-3 md:mb-4">
                <BarChart3 className="w-3.5 h-3.5" />
                {currentSlide + 1} OF {FEATURES.length}
              </span>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
                {FEATURES[currentSlide].title}
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                {FEATURES[currentSlide].description}
              </p>
            </div>
          </div>

          {/* Dot labels */}
          <div className="px-4 md:px-6 py-3 border-t border-[#1a3550] flex items-center justify-center gap-2 md:gap-4 flex-wrap">
            {FEATURES.map((f, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`flex items-center gap-1 md:gap-1.5 text-[10px] md:text-xs transition-colors ${
                  i === currentSlide
                    ? 'text-sky-400 font-semibold'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid={`carousel-dot-${i}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  i === currentSlide ? 'bg-sky-400' : 'bg-slate-600'
                }`} />
                <span className="hidden sm:inline">{f.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1 bg-[#1a3550] rounded-full max-w-[200px] mx-auto overflow-hidden">
          <div
            className="h-full bg-sky-500 rounded-full transition-all duration-300"
            style={{ width: `${((currentSlide + 1) / FEATURES.length) * 100}%` }}
          />
        </div>
      </section>

      {/* Feature Grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-center text-white mb-8">
          Everything You Need for Campaign Finance Analysis
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {GRID_FEATURES.map((f, i) => (
            <div
              key={i}
              className="bg-[#0f2035] border border-[#1a3550] rounded-xl p-4 text-center hover:border-sky-500/30 hover:bg-[#122a42] transition-colors group"
            >
              <f.icon className="w-7 h-7 mx-auto mb-2 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
              <div className="font-semibold text-sm text-white">{f.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <div className="bg-[#0f2035]/80 backdrop-blur-sm border border-[#1a3550] rounded-2xl p-10 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            Ready to Analyze Campaign Finance Data?
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            Upload FEC datasets and start uncovering patterns in political contributions.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-3 bg-sky-500 hover:bg-sky-400 text-white font-semibold rounded-full transition-colors text-base"
            data-testid="cta-get-started-btn"
          >
            Get Started
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1a3550]/50 py-6 text-center text-xs text-slate-500">
        &copy; 2025/2026 FEC Watchdog &mdash; Campaign Finance Intelligence
      </footer>
    </div>
  );
}
